import type { ImageAdjustments } from "@schoolmag/contracts";

export const DEFAULT_IMAGE_ADJUSTMENTS: ImageAdjustments = { brightness: 0, contrast: 0, saturation: 0, warmth: 0, grayscale: 0, blur: 0 };

export function imageFilterString(value: ImageAdjustments): string {
  const filters = [
    `brightness(${100 + value.brightness}%)`,
    `contrast(${100 + value.contrast}%)`,
    `saturate(${100 + value.saturation}%)`,
    `grayscale(${value.grayscale}%)`,
    `blur(${value.blur}px)`
  ];
  if (value.warmth > 0) filters.push(`sepia(${value.warmth}%)`, `saturate(${100 + Math.round(value.warmth * .4)}%)`);
  if (value.warmth < 0) filters.push("hue-rotate(180deg)", `sepia(${Math.abs(value.warmth)}%)`, "hue-rotate(180deg)");
  return filters.join(" ");
}

export function hasImageAdjustments(value: ImageAdjustments): boolean {
  return Object.values(value).some((item) => item !== 0);
}

export async function createAdjustedImage(source: Blob, filename: string, adjustments: ImageAdjustments): Promise<{ file: File; width: number; height: number }> {
  const bitmap = await createImageBitmap(source);
  const maxPixels = 32_000_000;
  const maxDimension = 8192;
  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height), Math.sqrt(maxPixels / (bitmap.width * bitmap.height)));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) { bitmap.close(); throw new Error("Image editing is not supported in this browser."); }
  context.filter = imageFilterString(adjustments);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const mimeType = source.type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error("The edited image could not be created.")), mimeType, .94));
  const base = filename.replace(/\.[^.]+$/, "").slice(0, 190) || "photo";
  const extension = mimeType === "image/png" ? "png" : "jpg";
  return { file: new File([blob], `${base}-edited.${extension}`, { type: mimeType }), width, height };
}
